
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Tooltip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Divider,
  PaginationItem
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import RoomIcon from '@mui/icons-material/Room';
import { isEqual } from 'lodash';
import { useNonPersistentFilterItemsStore } from "../assets/config"; // Keep your custom hook if needed
import service from "../Services/apiheader";
import Cookies from 'js-cookie';
import { accessToken } from '../api/apiFunctions';
import NoDataAvailable from "../components/NoDataAvailable";
import Loading from "../components/Loading";
import { t } from 'i18next';

type DateFilterParams = { dateFlag: boolean; fieldId: number; le?: number; ge?: number };
type SelectFilterParams = { field: string; value: (string | number)[] };
type SelectConditionParams = { field: string; value: (string | number)[] };
type DateConditionParams = { field: string; gte?: string; lte?: string };
type ConditionItem = SelectConditionParams | DateConditionParams;
type FilterParamsItem = SelectFilterParams | DateFilterParams;

export interface GlobalSearchParams {
  query: string;
  semantic_search: boolean;
  conditions: ConditionItem[];
}

interface ApplicationSource {
  sid: number;
  recordID: string;
  registrationID: string;
  authorizationNumber: string;
  productName: string;
  packageName: string;
  country: string;
  procedureType: string;
  xevmpdStatus: string;
}

interface Highlight {
  productPhase: string[];
}

interface Record {
  source: ApplicationSource;
  highlight: Highlight;
}

interface ApiResponse {
  total: number;
  page: number;
  size: number;
  records: Record[];
}

const GlobalSearchList = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState<Record[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  // const [search] = useSearchStore((state) => [state.search]);
  const prevSearchRef = useRef<GlobalSearchParams | null>();
  const [loading, setLoading] = useState(false);
  const tenantId = Cookies.get("USER_TENANT_ID") || "";
  const userId = Cookies.get("USER_EMAIL") || ""
  const domain = "All";
  const [search] = useNonPersistentFilterItemsStore((state: any) => [state.search]); 

  useEffect(() => {
    setPage(1);
    setRowsPerPage(50);
    console.log("search changed", search);
    fetchData();
  }, [search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await service.post(
        "xevmpd/v1/search",
        {
          page,
          pagesize: rowsPerPage,
          semantic_search: search?.semantic_search,
          query: search?.query,
          conditions:[],
          includes: [],
          excludes: []
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'tenant-id': tenantId,
            'user-id': userId,
            'Content-Type': 'application/json',
            'domain-name': domain,
            'module-name': 'xevmpd',
          },
        }
      );
      console.log(response)
      const result =  response;
      const highlightedRecords =result?.data?.data?.records?.map((item: any) => {
        const highlighted = { ...item, source: { ...item.source } };

        if (item.highlight) {
          for (const key in item.highlight) {
            if (highlighted.source.hasOwnProperty(key)) {
              highlighted.source[key] = item.highlight[key][0];
            }
          }
        }

        return highlighted;
      });
      setData(highlightedRecords || []);
      setTotalCount(result?.data?.data?.total || 0);
    } catch (error) {
      console.error('API fetch error:', error);
    }
    setLoading(false);
  };

  const viewApplication = async (rowItem: any) => {
    console.log(rowItem);
    const xevmpdid: string = rowItem.recordID.substring(rowItem.recordID.lastIndexOf("-") + 1);
    setLoading(true);
    window.open(
      `https://app.${process.env.REACT_APP_STG_DOMAIN}/xevmpd?modulePath=/xevmpd/view-xevmpd/${xevmpdid}`
    );
    setLoading(false);
  };
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    fetchData();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (isEqual(search, prevSearchRef.current)) return;
    prevSearchRef.current = search;
    fetchData();
  }, [search]);

  const handlePageChange = (_: any, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event: any) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  return (
    <>
    {(!data || data.length === 0) ? (
      <Box>
        <NoDataAvailable />
      </Box>
    ) : (
      <Box>
        {(data || []).map((item, index) => (
          <Grid
            container
            spacing={0}
            sx={{
              py: 2,
              borderBottom: '1px solid #e0e0e0',
              alignItems: 'flex-start',
              fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            }}
            key={index}
          >
            {/* Left Block */}
            <Grid item xs={12} md={9}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Tooltip title={item.source.recordID?.replace(/<mark>/g, '').replace(/<\/mark>/g, '')} arrow>
                  <Typography
                    component="a"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      viewApplication(item.source);
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      color: 'primary.main',
                      fontWeight: 500,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                    dangerouslySetInnerHTML={{ __html: item.source.recordID }}
                  />
                </Tooltip>
                <Tooltip title={item.source.productName?.replace(/<mark>/g, "")
                                  .replace(/<\/mark>/g, "")} arrow>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'primary.main',
                      maxWidth: 'calc(100% - 150px)',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: `- ${item.source.productName}`,
                    }}
                  />
                </Tooltip>
  
                <Box
                  sx={{
                    backgroundColor:
                         '#d9d9d9',
                    color: 'common.black',
                    fontWeight: 500,
                    borderRadius: '12px',
                    px: 1.5,
                    height: 24,
                    //ml: 'auto',
                    fontSize: '0.75rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    justifyContent: 'center',
                  }}
                  dangerouslySetInnerHTML={{ __html: (
                     item.source.xevmpdStatus
                    ) }}
                />
              </Box>

              
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '93%',
                  cursor: 'default',
                }}
              >
                <span style={{ color: '#878484' }}> Registration ID:&nbsp;</span>
                <Tooltip
                  title={item.source.registrationID?.replace(/<mark>/g, "")
                    .replace(/<\/mark>/g, "")}
                  arrow
                  followCursor
                  placement="bottom"
                >
                  <span style={{ color: '#333333', fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: item.source.registrationID }} />
                </Tooltip>
              </Box>
          

  
              <Stack
                direction="row"
                spacing={1}
                mt={0.5}
                alignItems="center"
                divider={<Divider orientation="vertical" flexItem />}
              >
               
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexBasis: '30%',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: '#878484' }}>Procedure Type:&nbsp;</span>
                  <Tooltip title={item.source.procedureType?.replace(/<mark>/g, "")
                    .replace(/<\/mark>/g, "")} arrow>
                    <span style={{ color: '#333333', fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: item.source.procedureType }} />
                  </Tooltip>
                </Typography>
  
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexBasis: '20%',
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: '#878484' }}>Package Name:&nbsp;</span>
                  <Tooltip title={item.source.packageName?.replace(/<mark>/g, "")
                    .replace(/<\/mark>/g, "")} arrow>
                    <span style={{ color: '#333333', fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: item.source.packageName }} />
                  </Tooltip>
                </Typography>
  
                
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flexBasis: '20%',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: '#878484' }}>Authorisation Number:&nbsp;</span>
                    <Tooltip title={item.source.authorizationNumber?.replace(/<mark>/g, "")
                                  .replace(/<\/mark>/g, "")} arrow>
                    <span style={{ color: '#333333', fontWeight: 500 }} dangerouslySetInnerHTML={{ __html: item.source.authorizationNumber }} />
                    </Tooltip>
                  </Typography>
  
                <Tooltip
                  title={`${item.source.country?.replace(/<mark>/g, "")
                      .replace(/<\/mark>/g, "")}`}
                  arrow
                  placement="bottom"
                   
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      maxWidth: 350, // adjust as needed
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      cursor: 'default',
                    }}
                  >
                    <RoomIcon fontSize="small" sx={{ mr: 0.5, flexShrink: 0 }} />
                    <Box
                      component="strong"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: `${item.source.country}`,
                      }}
                    />
                  </Box>
                </Tooltip>

              </Stack>
            </Grid>
  
            {/* Right Block */}
            {/* <Grid
              item
              xs={12}
              md={3}
              sx={{
                pl: 5,
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography fontSize="0.75rem" color="text.primary">
                Workflow Status:&nbsp;
                <strong dangerouslySetInnerHTML={{ __html: item.source.applicationStatus }} />
              </Typography>
            </Grid> */}
          </Grid>
        ))}
  
        {/* Pagination */}
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontSize="0.75rem" color="text.secondary">
              {t("Rows_per_page")}:
            </Typography>
  
            <FormControl size="small" variant="standard">
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                disableUnderline
                sx={{ minWidth: 60, fontSize: '0.75rem' }}
              >
                {[50,100,200].map((count) => (
                  <MenuItem key={count} value={count}>
                    {count}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
  
            <Typography fontSize="0.75rem" color="text.secondary">
              {Math.min((page - 1) * rowsPerPage + 1, totalCount)}–
              {Math.min(page * rowsPerPage, totalCount)} of {totalCount}
            </Typography>
  
            <Pagination
              count={Math.ceil(totalCount / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              siblingCount={0}
              boundaryCount={0}
              size="small"
              shape="rounded"
              variant="text"
              showFirstButton={false}
              showLastButton={false}
              renderItem={(item) => {
                if (item.type === 'previous' || item.type === 'next') {
                  return <PaginationItem {...item} />;
                }
                return null;
              }}
              sx={{
                '& .MuiPagination-ul': {
                  gap: 0.5,
                },
                '& .MuiPaginationItem-root': {
                  minWidth: 30,
                  height: 30,
                  fontSize: '0.75rem',
                },
              }}
            />
          </Stack>
        </Box>
      </Box>
    )}
    <Loading open={loading} />
  </>



  );
};

export default GlobalSearchList;



